import React, { useEffect, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { getFeedbackChannel, submitFeedback } from '../api/rttsClient';
import { STUDIO_COPY } from '../config';

type StudioFeedbackButtonProps = {
  variant?: 'default' | 'rail';
};

/**
 * Lightweight friends-beta feedback: in-product form + mailto fallback.
 */
const StudioFeedbackButton: React.FC<StudioFeedbackButtonProps> = ({
  variant = 'default',
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [mailto, setMailto] = useState(STUDIO_COPY.feedbackMailtoLabel);
  const [mailtoHref, setMailtoHref] = useState(
    'mailto:antoine@scoutlytics.gg?subject=RTTS%20Friends%20Beta%20Feedback'
  );
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void getFeedbackChannel()
      .then((cfg) => {
        if (cancelled) return;
        if (cfg.mailto) {
          setMailtoHref(cfg.mailto);
        }
      })
      .catch(() => {
        /* keep defaults */
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || !message.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await submitFeedback(message.trim());
      setDone(true);
      setMessage('');
    } catch {
      setError(STUDIO_COPY.feedbackError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`relative ${variant === 'rail' ? 'studio-feedback--rail' : ''}`}
      data-testid="studio-feedback"
    >
      <button
        type="button"
        className={
          variant === 'rail'
            ? 'studio-rail-item studio-rail-item--button'
            : 'studio-btn-ghost text-sm px-3 py-1.5'
        }
        data-testid="studio-feedback-open"
        onClick={() => {
          setOpen((v) => !v);
          setDone(false);
          setError(null);
        }}
      >
          {variant === 'rail' ? (
            <>
              <span className="studio-rail-icon" aria-hidden>
                <MessageSquare size={20} strokeWidth={1.75} />
              </span>
              <span className="studio-rail-label">{STUDIO_COPY.feedbackCta}</span>
            </>
          ) : (
            STUDIO_COPY.feedbackCta
          )}
        </button>
        {open ? (
          <div
            className={`w-72 sm:w-80 rounded-lg border border-[var(--studio-border)] bg-[var(--studio-bg-elevated)] p-4 shadow-lg z-20 space-y-3 ${
              variant === 'rail'
                ? 'studio-feedback-panel--rail'
                : 'absolute right-0 mt-2'
            }`}
          data-testid="studio-feedback-panel"
          role="dialog"
          aria-label={STUDIO_COPY.feedbackHeadline}
        >
          <div className="space-y-1">
            <p className="font-medium text-sm">{STUDIO_COPY.feedbackHeadline}</p>
            <p className="text-xs text-[var(--studio-muted)] leading-relaxed">
              {STUDIO_COPY.feedbackBlurb}
            </p>
          </div>
          {done ? (
            <p className="text-sm text-[var(--studio-accent)]" data-testid="studio-feedback-thanks">
              {STUDIO_COPY.feedbackThanks}
            </p>
          ) : (
            <form className="space-y-3" onSubmit={(e) => void handleSubmit(e)}>
              <textarea
                className="w-full min-h-[88px] rounded-md border border-[var(--studio-border)] bg-black/20 px-3 py-2 text-sm"
                placeholder={STUDIO_COPY.feedbackPlaceholder}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="studio-feedback-message"
                maxLength={4000}
                required
              />
              {error ? (
                <p className="text-xs text-[var(--studio-danger)]" role="alert">
                  {error}
                </p>
              ) : null}
              <div className="flex items-center justify-between gap-2">
                <a
                  href={mailtoHref}
                  className="text-xs text-[var(--studio-muted)] underline"
                  data-testid="studio-feedback-mailto"
                >
                  {mailto}
                </a>
                <button
                  type="submit"
                  className="studio-btn-primary text-sm px-3 py-1.5"
                  disabled={busy || !message.trim()}
                  data-testid="studio-feedback-submit"
                >
                  {busy ? STUDIO_COPY.feedbackSending : STUDIO_COPY.feedbackSubmit}
                </button>
              </div>
            </form>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default StudioFeedbackButton;
